import { di } from 'react-magnetic-di';
import type { StoreActionApi } from 'react-sweet-state';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import { fireOperationalEvent } from '@atlassian/ui-modifications-analytics/utils/fire-operational-event';

import { selectReadyPublicDataForOnInit } from '../../hooks/use-ready-fields/utils';
import type { IssueAdjustmentsState, StoreContainerProps } from '../../types';
import { setError } from '../errors';
import type { FieldsInitialisationStatus } from './types';

const getTimeout = () => 50000;

const getFieldsInitializationTime = (startTime: number | null) =>
	startTime !== null ? performance.now() - startTime : null;

const isFieldInitialisationComplete = (status: FieldsInitialisationStatus) =>
	status === 'success' || status === 'timedOut';

export const startFieldsInitialisationTimeout =
	({ createAnalyticsEvent }: { createAnalyticsEvent: CreateUIAnalyticsEvent }) =>
	({ setState, getState, dispatch }: StoreActionApi<IssueAdjustmentsState>): void => {
		const { fieldsInitialization } = getState();

		if (
			fieldsInitialization.timeoutId === undefined &&
			fieldsInitialization.status === 'notStarted'
		) {
			const timeoutId = setTimeout(
				() => dispatch(failFieldInitilisation({ createAnalyticsEvent })),
				getTimeout(),
			);

			setState({
				fieldsInitialization: {
					startTime: performance.now(),
					timeoutId,
					status: 'inProgress',
				},
			});
		}
	};

export const failFieldInitilisation =
	({ createAnalyticsEvent }: { createAnalyticsEvent: CreateUIAnalyticsEvent }) =>
	(
		{ getState, setState, dispatch }: StoreActionApi<IssueAdjustmentsState>,
		{ viewType }: StoreContainerProps,
	): void => {
		di(fireOperationalEvent);
		const { formData, experienceDataIsLoading, numberOfSupportedFields, fieldsInitialization } =
			getState();
		const { startTime, status } = fieldsInitialization;
		if (
			isFieldInitialisationComplete(status) ||
			selectReadyPublicDataForOnInit(getState(), { viewType }) !== null
		) {
			return;
		}

		dispatch(setError({ errorCode: 'ON_INIT_FIELDS_INITIALIZATION_TIMEOUT' }));

		fireOperationalEvent(createAnalyticsEvent, {
			action: 'timedOut',
			actionSubject: 'jiraUiModifications.fieldsInitialization',
			attributes: {
				previousStatus: status,
				configuredTimeout: getTimeout(),
				fieldsInitializationTime: getFieldsInitializationTime(startTime),
				initalizedFieldsCount: Object.keys(formData ?? {}).length,
				experienceDataIsLoading,
				supportedFieldsCount: numberOfSupportedFields,
				viewType,
			},
		});

		setState({
			fieldsInitialization: {
				...fieldsInitialization,
				status: 'timedOut',
			},
		});
	};

export const successFieldsInitalisation =
	({ createAnalyticsEvent }: { createAnalyticsEvent: CreateUIAnalyticsEvent }) =>
	(
		{ getState, setState }: StoreActionApi<IssueAdjustmentsState>,
		{ viewType }: StoreContainerProps,
	): void => {
		di(fireOperationalEvent);
		const { status } = getState().fieldsInitialization;

		if (isFieldInitialisationComplete(status)) {
			return;
		}

		const { numberOfSupportedFields, fieldsInitialization } = getState();
		const { timeoutId, startTime } = fieldsInitialization;

		clearTimeout(timeoutId);

		fireOperationalEvent(createAnalyticsEvent, {
			action: 'success',
			actionSubject: 'jiraUiModifications.fieldsInitialization',
			attributes: {
				previousStatus: status,
				fieldsInitializationTime: getFieldsInitializationTime(startTime),
				initalizedFieldsCount: numberOfSupportedFields,
				viewType,
			},
		});

		setState({
			fieldsInitialization: {
				...fieldsInitialization,
				status: 'success',
			},
		});
	};
